import { Fragment } from "react";
import type { CardDoCatalogo } from "../lib/adminCatalogo";

/**
 * As peças compartilhadas da aba Catálogo (000262).
 *
 * Elas moram aqui, e não em cada tela, porque as três as usam: a Visão geral, a
 * página do produto e a da ferramenta. Duas cópias de `situacaoDaFerramenta`
 * divergiriam, e a que divergisse em silêncio seria a que ninguém está olhando
 * — é a cicatriz da explicação do teto do MEI, calculada por fora do cálculo e
 * contradizendo o número em quatro situações medidas.
 */

/**
 * A SITUAÇÃO de uma ferramenta, DERIVADA — nunca guardada.
 *
 * A equipe de produtos pediu o campo "status (ativa, em construção ou
 * descontinuada)". Ele não virou coluna porque as duas que o respondem já
 * existem e são as que MANDAM: `ativo` decide se o card aparece na vitrine, e
 * `destino = 'em-breve'` é a tela dizendo ao aluno que a ferramenta ainda vem.
 *
 * Guardado, o status passaria a poder mentir exatamente onde a derivação não
 * vale — que é o defeito do campo `tipo` do catálogo de CNAEs, derivado do
 * anexo e gravado, rotulando 122 atividades de cultivo como "Comércio".
 *
 * **Desde 21/09 ele é EDITÁVEL na tela da ferramenta, e continua derivado.**
 * Victor pediu que o time pudesse escolher o status; o que a tela faz é
 * traduzir a escolha nos dois campos que mandam (`camposDaSituacao`, abaixo) e
 * salvar pela RPC que já existe. Escolher o status É escolher o que o aluno vê,
 * que é o único jeito de os dois nunca divergirem.
 */
export function situacaoDaFerramenta(c: CardDoCatalogo): {
  chave: "descontinuada" | "em-construcao" | "ativa";
  rotulo: string;
  classe: string;
  diz: string;
} {
  if (!c.ativo) {
    return {
      chave: "descontinuada",
      rotulo: "descontinuada",
      classe: "bg-chip text-chip-tinta",
      diz: "o card está desativado, então não aparece na vitrine de ninguém",
    };
  }
  if (c.destino === "em-breve") {
    return {
      chave: "em-construcao",
      rotulo: "em construção",
      // `alerta` é a família de ATENÇÃO (laranja); `aviso` desta casa é VERDE,
      // de confirmação — e um aviso verde comunica "está tudo bem", que é o
      // oposto da mensagem. E `alerta` não tem `-borda`: seria a 15ª classe
      // fantasma que a `tokens.mjs` acha.
      classe: "bg-alerta-fundo text-alerta-tinta",
      diz: "o card aparece para o aluno e a tela diz que a ferramenta ainda vem",
    };
  }
  return {
    chave: "ativa",
    rotulo: "ativa",
    classe: "bg-aviso-fundo text-aviso-tinta",
    diz: "abre para quem tem o produto",
  };
}

/**
 * Este card é uma FERRAMENTA que um curso entrega?
 *
 * Três telas precisam da mesma resposta — a Visão geral (que conta), a página do
 * produto (que lista) e a de Ferramentas (que liga e desliga) —, e por algumas
 * horas em 21/09 a regra esteve escrita em duas delas e faltando na terceira: os
 * módulos da FBC sumiram da lista de ligar e continuaram aparecendo em «O que
 * este curso entrega», com a contagem do curso somando os dois.
 *
 * O que fica de fora, e por quê:
 *
 *   · **universal** — Aulas e Comunidade valem em TODO curso, sem passar por
 *     `produto_cards`; contá-los por curso seria contar a mesma coisa 12 vezes.
 *   · **escopo `conta`** — o Contrate um Contador aparece uma vez na tela
 *     inicial e não pertence a curso nenhum.
 *   · **`tipo = 'modulo'`** — Minhas Notas e Secretaria do Aluno são fixos em
 *     todas as pós e no MBA (decisão de Victor, 21/09). Citá-los por curso é
 *     repetir nove vezes o que vale igual.
 *
 * Quem separa é o BANCO, sempre: `universal`, `escopo` e `tipo` são colunas de
 * `cards`. Uma lista de slugs escrita aqui divergiria no dia do terceiro módulo.
 */
export function ehFerramentaDeCurso(c: CardDoCatalogo): boolean {
  return !c.universal && c.escopo !== "conta" && c.tipo !== "modulo";
}

/** As três situações que uma ferramenta pode ter, na ordem em que a tela as lista. */
export type ChaveDaSituacao = "ativa" | "em-construcao" | "descontinuada";

export const SITUACOES: Array<{ chave: ChaveDaSituacao; rotulo: string; diz: string }> = [
  { chave: "ativa", rotulo: "Ativa", diz: "abre para quem tem o produto" },
  {
    chave: "em-construcao",
    rotulo: "Em construção",
    diz: "aparece na vitrine dizendo que ainda vem",
  },
  {
    chave: "descontinuada",
    rotulo: "Descontinuada",
    diz: "some da vitrine — tira a ferramenta de quem pagou, e o banco cobra o porquê",
  },
];

/**
 * A tradução INVERSA de `situacaoDaFerramenta`: a situação escolhida vira os
 * dois campos que mandam.
 *
 * `destinoAtual` entra porque "ativa" não tem endereço próprio — ela é qualquer
 * destino que não seja `em-breve`. Adivinhar um (`interno`, digamos) seria a
 * tela decidindo POR ONDE o aluno abre a ferramenta, que é a única coisa aqui
 * que ninguém consegue conferir olhando. Então ela devolve o destino que já
 * estava, e quem precisa trocá-lo tem o campo ao lado.
 */
export function camposDaSituacao(
  chave: ChaveDaSituacao,
  destinoAtual: string,
): { ativo: boolean; destino: string } {
  if (chave === "descontinuada") return { ativo: false, destino: destinoAtual };
  if (chave === "em-construcao") return { ativo: true, destino: "em-breve" };
  return { ativo: true, destino: destinoAtual };
}

/**
 * Tira a MARCAÇÃO do markdown do ClickUp, deixando o texto.
 *
 * ---------------------------------------------------------------------------
 * Por que aqui, e não na carga
 * ---------------------------------------------------------------------------
 * O que está no banco é ESPELHO da origem, e espelho se guarda como a origem
 * escreveu. Limpar na carga seria decidir, de uma vez e para sempre, que o hub
 * nunca vai renderizar markdown — e a primeira vez que alguém quisesse o
 * negrito de volta teria de recarregar as onze páginas.
 *
 * Limpar aqui é presentação, e é reversível.
 *
 * ---------------------------------------------------------------------------
 * O que ela faz, e o que ela NÃO faz
 * ---------------------------------------------------------------------------
 * Ela derruba `**`, `__` e o escape de asterisco, troca o marcador de lista por
 * `•` e reduz `[texto](url)` ao texto — ou à url, quando o texto É a url, que é
 * como o ClickUp escreve link nu.
 *
 * O que ela NÃO faz é produzir HTML. Este painel não tem
 * `dangerouslySetInnerHTML` — o hub inteiro não tem, e a ausência é a peça de
 * segurança. O texto vem de um sistema de terceiro e vai para uma tela de
 * admin; injetá-lo como marcação seria trocar uma linha feia por um vetor.
 */
export function semMarcacao(texto: string): string {
  return String(texto ?? "")
    // O escape primeiro: a origem escreve `Duração\*\*:` e, tirando `**` antes,
    // a barra sobrevive no meio dos dois asteriscos e o par nunca casa.
    .replace(/\\([*_[\]()\-#])/g, "$1")
    .replace(/\[([^\]]*)\]\(([^)]*)\)/g, (_, rotulo: string, url: string) => {
      const r = rotulo.trim();
      return !r || /^https?:\/\//.test(r) ? url : r;
    })
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    // O marcador de lista vira bolinha. Com aninhamento, a origem escreve
    // "*       *   texto" — o laço colapsa a sequência inteira numa bolinha só,
    // porque a profundidade não sobrevive a um `whitespace-pre-line`.
    .replace(/^[ \t]*(?:[*+-][ \t]+)+/gm, "• ");
}

/**
 * Texto corrido virando parágrafos, com a marcação limpa.
 *
 * `whitespace-pre-line` preserva as quebras de dentro do parágrafo — as listas
 * da origem dependem disso — e o `split` em linha em branco separa os blocos.
 */
export function Prosa({ texto }: { texto: string }) {
  // O escape e a bolinha de lista vão sobre o texto INTEIRO, antes de achar os
  // links: a bolinha depende do começo da LINHA, e aplicada a um pedaço depois
  // de um link ela transformaria " - texto" em "• texto" no meio da frase.
  const base = String(texto ?? "")
    .replace(/\\([*_[\]()\-#])/g, "$1")
    .replace(/^[ \t]*(?:[*+-][ \t]+)+/gm, "• ");
  const paragrafos = base
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <div className="space-y-2">
      {paragrafos.map((p, i) => (
        <p key={i} className="whitespace-pre-line text-sm leading-relaxed text-cinza">
          {pedacosDaProsa(p).map((q, j) =>
            "url" in q ? (
              <a key={j} href={q.url} target="_blank" rel="noreferrer" className="break-all text-link underline">
                {q.rotulo}
                <span className="sr-only"> (abre em outra aba)</span>
              </a>
            ) : (
              <Fragment key={j}>{q.texto}</Fragment>
            ),
          )}
        </p>
      ))}
    </div>
  );
}

/**
 * Um parágrafo da origem em PEDAÇOS: texto e link.
 *
 * Até 24/09 a Prosa reduzia `[texto](url)` ao texto, e o link do Drive das
 * páginas de produto (33 ocorrências em 8 cursos) virava uma palavra que não
 * abria nada — e, quando o rótulo não era a url, a url sumia da tela. Achado na
 * conferência do Plano de Melhorias de Produtos.
 *
 * Só vira `<a>` o que começa com `https://`: o texto vem de um sistema de
 * terceiro, e um `javascript:` ou um `http://` num `href` é o vetor que esta
 * casa nunca abre (a mesma razão de `profiles.redes` guardar o handle). O resto
 * fica como TEXTO, e o React o escapa. Nada de HTML, como na `semMarcacao`.
 */
export function pedacosDaProsa(paragrafo: string): Array<{ texto: string } | { rotulo: string; url: string }> {
  const limpar = (t: string) => t.replace(/\*\*/g, "").replace(/__/g, "");
  const pedacos: Array<{ texto: string } | { rotulo: string; url: string }> = [];
  let ultimo = 0;
  for (const m of paragrafo.matchAll(/\[([^\]]*)\]\(([^)]*)\)/g)) {
    const [inteiro, rotulo, url] = m;
    const i = m.index ?? 0;
    if (i > ultimo) pedacos.push({ texto: limpar(paragrafo.slice(ultimo, i)) });
    const u = url.trim();
    const r = limpar(rotulo).trim();
    if (/^https:\/\//i.test(u)) pedacos.push({ rotulo: !r || /^https?:\/\//i.test(r) ? u : r, url: u });
    else pedacos.push({ texto: !r || /^https?:\/\//i.test(r) ? u : r });
    ultimo = i + inteiro.length;
  }
  if (ultimo < paragrafo.length) pedacos.push({ texto: limpar(paragrafo.slice(ultimo)) });
  return pedacos;
}
